/**
 * Keeps this device's save and the cloud save together (docs/plans/007b-account-client.md). Playing never needs an account; when
 * signed in, every change is pushed a few seconds later with the revision last seen. Nothing is overwritten without the player's
 * choice: the only automatic cases are "no cloud save yet" (upload), "this device has no progress" (take the cloud's) and "identical".
 */
import { deserialize, serialize, type SaveGame } from "@zookeepbit/sim";
import { AccountError, type AccountApi, type AccountErrorCode, type Birth } from "./api";

export type SyncStatus = "idle" | "syncing" | "synced" | "not-synced" | "conflict";

export interface AccountState {
  signedIn: boolean;
  username: string | null;
  status: SyncStatus;
  /** The last problem, for the screen to explain. Cleared by the next success. */
  error: AccountErrorCode | null;
  /** A sign-in, sign-up, deletion or conflict choice is running. */
  busy: boolean;
  /** The account was made with Google and has no password (deleting it asks for the username). */
  viaGoogle: boolean;
}

/**
 * What this device last agreed on with the account: the cloud revision and the saved text at that moment. It lets a later start tell
 * "this device is just ahead" (every page open adds an event to the save) from "someone else changed the cloud".
 */
export interface SyncBase {
  /** Lower-case username the base belongs to. */
  username: string;
  revision: number;
  text: string;
}

export interface SyncDeps {
  api: AccountApi;
  /** Where the base is kept between page loads (localStorage). Defaults to memory. */
  base?: { load(): SyncBase | null; save(base: SyncBase | null): void };
  device: { read(): SaveGame; adopt(save: SaveGame): void };
  timers: { set(fn: () => void, ms: number): number; clear(id: number): void };
  onChange(): void;
}

export const PUSH_DELAY_MS = 5_000;

/** Settings alone are not progress: a fresh device with a changed setting must still take the cloud save, not ask. */
export function hasProgress(save: SaveGame): boolean {
  return save.pet !== null || save.book.length > 0 || save.lastHatchedWeekId !== null;
}

const envelopeOf = (save: SaveGame): unknown => JSON.parse(serialize(save)) as unknown;

function codeOf(error: unknown): AccountErrorCode {
  return error instanceof AccountError ? error.code : "server_error";
}

export function createSync(deps: SyncDeps) {
  const { api, device, timers } = deps;
  let memoryBase: SyncBase | null = null;
  const baseStore = deps.base ?? { load: () => memoryBase, save: (b: SyncBase | null) => void (memoryBase = b) };
  /** Remember what was just agreed with the cloud. */
  const remember = (text: string) => {
    if (state.username !== null && revision !== null) baseStore.save({ username: state.username.toLowerCase(), revision, text });
  };
  let state: AccountState = { signedIn: false, username: null, status: "idle", error: null, busy: false, viaGoogle: false };
  let revision: number | null = null;
  let lastSynced: string | null = null;
  let timer: number | null = null;
  let inFlight: Promise<void> | null = null;
  let dirty = false;
  let conflict: { cloud: SaveGame; revision: number } | null = null;

  const set = (patch: Partial<AccountState>) => {
    state = { ...state, ...patch };
    deps.onChange();
  };

  function forget(error: AccountErrorCode | null) {
    if (timer !== null) timers.clear(timer);
    timer = null;
    revision = null;
    lastSynced = null;
    dirty = false;
    conflict = null;
    baseStore.save(null);
    set({ signedIn: false, username: null, status: "idle", error, busy: false, viaGoogle: false });
  }

  function failed(error: unknown) {
    const code = codeOf(error);
    if (code === "not_signed_in") forget(code);
    else set({ status: "not-synced", error: code });
  }

  /** Compares this device with the cloud and either settles it or opens a conflict. */
  async function reconcile(): Promise<void> {
    set({ status: "syncing", error: null });
    const cloud = await api.getSave();
    const local = device.read();
    if (cloud === null) {
      revision = null;
      await push(true);
      return;
    }
    const parsed = deserialize(JSON.stringify(cloud.envelope));
    if (!parsed.ok) {
      // A save from a newer game, or a broken one: never touch it, never overwrite it.
      set({ status: "not-synced", error: "invalid_save" });
      return;
    }
    const base = baseStore.load();
    const mine = base !== null && state.username !== null && base.username === state.username.toLowerCase() ? base : null;
    if (serialize(parsed.data) === serialize(local)) {
      revision = cloud.revision;
      lastSynced = serialize(local);
      remember(lastSynced);
      set({ status: "synced" });
    } else if (mine && mine.revision === cloud.revision) {
      // The cloud has not moved since this device last synced: the device is just ahead. Upload it.
      revision = cloud.revision;
      lastSynced = mine.text;
      await push(true);
    } else if (!hasProgress(local) || (mine && mine.text === serialize(local))) {
      // Nothing on this device that the cloud lacks: take the cloud's game.
      device.adopt(parsed.data);
      revision = cloud.revision;
      lastSynced = serialize(parsed.data);
      remember(lastSynced);
      set({ status: "synced" });
    } else {
      conflict = { cloud: parsed.data, revision: cloud.revision };
      if (timer !== null) timers.clear(timer);
      timer = null;
      set({ status: "conflict" });
    }
  }

  /** One upload at a time; changes made meanwhile are picked up when it ends. */
  function push(force = false): Promise<void> {
    if (inFlight) return inFlight;
    if (!state.signedIn || conflict) return Promise.resolve();
    const text = serialize(device.read());
    if (!force && text === lastSynced) {
      dirty = false;
      return Promise.resolve();
    }
    dirty = false;
    set({ status: "syncing", error: null });
    inFlight = (async () => {
      try {
        const res = await api.putSave(envelopeOf(device.read()), revision);
        revision = res.revision;
        lastSynced = text;
        remember(text);
        set({ status: "synced" });
      } catch (error) {
        if (codeOf(error) === "stale_save") await reconcile().catch(failed);
        else failed(error);
      } finally {
        inFlight = null;
      }
      if (state.signedIn && !conflict && state.status === "synced" && serialize(device.read()) !== lastSynced) schedule();
    })();
    return inFlight;
  }

  function schedule() {
    if (timer !== null) return;
    timer = timers.set(() => {
      timer = null;
      void push();
    }, PUSH_DELAY_MS);
  }

  async function enter(account: { username: string }, viaGoogle = false): Promise<void> {
    set({ signedIn: true, username: account.username, error: null, viaGoogle });
    try {
      await reconcile();
    } catch (error) {
      failed(error);
    }
  }

  return {
    state: (): AccountState => state,

    /** At page start: if a session cookie is still good, carry on as that account. Failing quietly keeps the game playable offline. */
    async start(): Promise<void> {
      try {
        const me = await api.me();
        if (me) await enter(me, me.hasPassword === false);
      } catch {
        // Offline or the server is down: stay signed out; the player can try again from Settings.
      }
    },

    async signIn(username: string, password: string): Promise<AccountErrorCode | null> {
      set({ busy: true, error: null });
      try {
        await enter(await api.login(username, password));
        set({ busy: false });
        return null;
      } catch (error) {
        const code = codeOf(error);
        set({ busy: false, error: code });
        return code;
      }
    },

    async signUp(username: string, password: string, birth: Birth): Promise<AccountErrorCode | null> {
      set({ busy: true, error: null });
      try {
        await enter(await api.register(username, password, birth));
        set({ busy: false });
        return null;
      } catch (error) {
        const code = codeOf(error);
        set({ busy: false, error: code });
        return code;
      }
    },

    /** After `?google=pending`: the age gate creates the account and signs in. */
    async finishGoogle(birth: Birth): Promise<AccountErrorCode | null> {
      set({ busy: true, error: null });
      try {
        await enter(await api.completeGoogle(birth), true);
        set({ busy: false });
        return null;
      } catch (error) {
        const code = codeOf(error);
        set({ busy: false, error: code });
        return code;
      }
    },

    async deleteGoogleAccount(confirmUsername: string): Promise<AccountErrorCode | null> {
      set({ busy: true, error: null });
      try {
        await api.deleteGoogleAccount(confirmUsername);
        forget(null);
        return null;
      } catch (error) {
        const code = codeOf(error);
        set({ busy: false, error: code });
        return code;
      }
    },

    async signOut(): Promise<void> {
      if (state.signedIn && dirty) await push();
      try {
        await api.logout();
      } catch {
        // The cookie may stay until it expires; this device forgets the account anyway.
      }
      forget(null);
    },

    /** Call after every local save. */
    localChanged(): void {
      if (!state.signedIn || conflict) return;
      dirty = true;
      schedule();
    },

    /** Upload now (the tab is being hidden, or the player is signing out). */
    async flush(): Promise<void> {
      if (timer !== null) timers.clear(timer);
      timer = null;
      if (!state.signedIn) return;
      await push();
    },

    /** The player's choice after a conflict. */
    async resolve(choice: "cloud" | "device"): Promise<void> {
      if (!conflict) return;
      const pending = conflict;
      conflict = null;
      set({ busy: true });
      try {
        if (choice === "cloud") {
          device.adopt(pending.cloud);
          revision = pending.revision;
          lastSynced = serialize(pending.cloud);
          remember(lastSynced);
          set({ status: "synced", error: null });
        } else {
          revision = pending.revision;
          await push(true);
        }
      } catch (error) {
        failed(error);
      }
      set({ busy: false });
    },

    exportData: () => api.exportData(),

    async deleteAccount(password: string): Promise<AccountErrorCode | null> {
      set({ busy: true, error: null });
      try {
        await api.deleteAccount(password);
        forget(null);
        return null;
      } catch (error) {
        const code = codeOf(error);
        set({ busy: false, error: code });
        return code;
      }
    },
  };
}

export type Sync = ReturnType<typeof createSync>;
